import { z } from 'zod';
import type { StaffRole } from '../../../../packages/contracts/src/index.js';

export const language = 'es-MX';

export const configureLanguage = () => {
  z.config(z.locales.es());
};

export const copy = {
  common: {
    loading: 'Cargando',
    notSet: 'Sin definir',
    unknown: 'Desconocido',
    noNotes: 'Sin notas',
    refresh: 'Actualizar',
    actions: 'Acciones',
    status: 'Estado',
    name: 'Nombre',
    phone: 'Teléfono',
    email: 'Correo',
    description: 'Descripción',
    category: 'Categoría',
    role: 'Rol',
    user: 'Usuario',
    active: 'Activo',
    inactive: 'Inactivo',
    disabled: 'Deshabilitado',
    delete: 'Eliminar',
    cannotUndo: 'Esta acción no se puede deshacer.',
  },
  app: {
    staffOperations: 'Operaciones del equipo',
    signedIn: 'Sesión iniciada',
    closeNavigation: 'Cerrar navegación',
    openNavigation: 'Abrir navegación',
    operationsTitle: 'Operaciones de club, hotel y spa',
    staffConsole: 'Consola del equipo de Happy Bark',
    signOut: 'Cerrar sesión',
    nav: {
      dashboard: 'Panel',
      customers: 'Clientes',
      dogs: 'Perros',
      stays: 'Hotel',
      services: 'Servicios',
      users: 'Usuarios',
    },
  },
  landing: {
    subtitle: 'Club, hotel y spa',
    services: 'Servicios',
    visit: 'Visita',
    staff: 'Equipo',
    eyebrow: 'Cuidado boutique para perros',
    headline: 'Happy Bark Happy Life',
    body: 'Un club, hotel y spa cálido para perros que merecen cuidado atento, rutinas suaves y un lugar hermoso y bien atendido.',
    planVisit: 'Planear una visita',
    staffConsole: 'Consola del equipo',
    highlights: {
      suites: 'Suites tranquilas',
      rhythm: 'Rutina diaria',
      notes: 'Notas de cuidado',
    },
    heroAlt: 'Logotipo de Happy Bark Club Hotel Spa',
    tiles: {
      club: { label: 'Club', value: 'Juego' },
      hotel: { label: 'Hotel', value: 'Descanso' },
      spa: { label: 'Spa', value: 'Brillo' },
    },
    careMenu: 'Menú de cuidado',
    servicesHeading: 'Cada visita tiene un propósito.',
    servicesBody: 'Desde fines de semana largos hasta días de baño, Happy Bark mantiene una experiencia pulida, personal y fácil de confiar.',
    serviceCards: [
      {
        title: 'Club',
        body: 'Diseñado para que tu perro se desarrolle física, mental y socialmente. Cada día tendrá diferentes actividades que podrán a prueba y mejorarán sus habilidades; el área física contempla prácticas de recorridos, juegos, fuerza, coordinación y nuestro producto estrella “la playa” para refrescarse y aprender a nadar. Para el desarrollo mental se contemplan destrezas, rompecabezas y retos que requerirán toda su atención, esto hará que termine completamente agotado. En ambas disciplinas socializará y aprenderá por imitación de otros perros que van realizando las actividades siempre enfocadas en su propio ritmo y personalidad. Adicionalmente festejamos los días de cumpleaños y fechas importantes; son el pretexto perfecto para siempre estar alegres. Si tu perro necesita un empujón adicional, como complemento tendremos entrenamientos enfocados a cada una de sus necesidades.',
      },
      {
        title: 'Hotel',
        body: 'Con un concepto totalmente nuevo y que estamos seguros que tu perro disfrutará al máximo, construimos unas cabañas enfocadas en tener suficiente espacio y confort para nuestros amigos caninos. En su estancia disfrutarán de cada una de las actividades del club, su alimentación y cuidado serán como en casa y a la hora de dormir su alojamiento contempla una cabaña con terraza y patio para que la libertad de movimiento haga de su estadía una mejor experiencia. Cada uno de nuestros huéspedes previo a su salida disfrutará de nuestro spa para que regrese reluciente a su hogar.',
      },
      {
        title: 'Spa',
        body: 'Creado para que tu mejor amigo siempre esté guapérrim@ y que además de todo disfrute todo el proceso mientras nuestras manos expertas lo atienden. Los servicios se diseñan y especializan para cada tipo de perro y sus diferentes necesidades. Puede ser desde un baño básico, hidratante, hipoalergénico o blanco reluciente para estar limpio y oler delicioso; los cortes de tu peludo se realizarán con las mejores técnicas y equipo necesario.',
      },
      {
        title: 'Plus',
        body: 'Tu tranquilidad es muy importante para nosotros, es por esto que al final del día contarás con un reporte del comportamiento y estado de ánimo de tu perro en nuestras instalaciones con fotografías y videos. Adicionalmente queremos facilitarte el día a día, es por eso que nuestro servicio de pickup es gratuito, recogemos y regresamos a tu peludo desde y hasta la puerta de tu casa.',
      },
    ],
    startHere: 'Empieza aquí',
    firstVisitHeading: 'Una primera visita debe sentirse simple.',
    firstVisitBody: 'Comparte las rutinas, preferencias y notas de cuidado de tu perro. La consola del equipo mantiene cada estancia organizada detrás de escena.',
    reservationsTitle: 'Reservaciones y notas de cuidado',
    reservationsBody: 'Gestionadas de forma segura por el equipo de Happy Bark.',
    locationTitle: 'Ubicación',
    locationBody: 'Abrir Happy Bark en Google Maps.',
    locationUrl: 'https://maps.app.goo.gl/RA1vEz1zrcTsqR5B9?g_st=iw',
    placeTitle: 'Club, hotel y spa',
    placeBody: 'Un solo lugar para jugar, descansar y recibir estética.',
  },
  auth: {
    setupRequired: 'Configuración de autenticación requerida',
    setupBody: 'Define las variables de entorno web de Cognito antes de usar la consola del equipo.',
    signInTitle: 'Consola del equipo de Happy Bark',
    signInBody: 'Inicia sesión con tu cuenta del equipo para gestionar clientes, perros, hotel, servicios y reportes.',
    signIn: 'Iniciar sesión',
    signingIn: 'Iniciando sesión',
    finishingSignIn: 'Finalizando inicio de sesión seguro',
    missingDetails: 'Cognito no devolvió los datos esperados para iniciar sesión.',
    failed: 'No se pudo iniciar sesión.',
  },
  dashboard: {
    title: 'Panel',
    body: 'Operaciones diarias de un vistazo.',
    loading: 'Cargando panel',
    metrics: {
      currentlyStaying: 'Hospedados ahora',
      todaysCheckIns: 'Entradas de hoy',
      todaysCheckOuts: 'Salidas de hoy',
      occupancy: 'Ocupación',
      pendingGrooming: 'Estética pendiente',
      revenueToday: 'Ingresos de hoy',
      revenueMonth: 'Ingresos del mes',
    },
    recentActivity: 'Actividad reciente',
    noRecentActivity: 'Aún no hay actividad reciente.',
    alerts: 'Alertas',
    noActiveAlerts: 'No hay alertas activas.',
  },
  customers: {
    title: 'Clientes',
    body: 'Registros de dueños, datos de contacto, notas y contactos de emergencia.',
    newCustomer: 'Nuevo cliente',
    firstName: 'Nombre',
    lastName: 'Apellido',
    address: 'Dirección',
    emergencyContact: 'Contacto de emergencia',
    notes: 'Notas',
    create: 'Crear cliente',
    directory: 'Directorio de clientes',
    loading: 'Cargando clientes',
    emptyTitle: 'Aún no hay clientes',
    emptyBody: 'Crea el primer registro de dueño para empezar a programar perros.',
    emergency: 'Emergencia',
    deleteConfirm: (name: string) => `¿Eliminar a ${name}? ${copy.common.cannotUndo}`,
    deleteLabel: (name: string) => `Eliminar a ${name}`,
  },
  dogs: {
    title: 'Perros',
    body: 'Perfiles de cuidado, notas médicas, instrucciones de alimentación y estado.',
    newDog: 'Nuevo perro',
    owner: 'Dueño',
    selectOwner: 'Selecciona un dueño',
    breed: 'Raza',
    birthDate: 'Fecha de nacimiento',
    weightKg: 'Peso kg',
    sex: 'Sexo',
    feedingInstructions: 'Instrucciones de alimentación',
    medicalConditions: 'Condiciones médicas',
    create: 'Crear perro',
    registry: 'Registro de perros',
    loading: 'Cargando perros',
    emptyTitle: 'Aún no hay perros',
    emptyBody: 'Agrega un perfil de perro después de crear su dueño.',
    care: 'Cuidado',
    deleteConfirm: (name: string) => `¿Eliminar a ${name}? ${copy.common.cannotUndo}`,
    deleteLabel: (name: string) => `Eliminar a ${name}`,
  },
  stays: {
    title: 'Hotel',
    body: 'Reservaciones, entradas, salidas, asignación de habitaciones y notas de cuidado diario.',
    createReservation: 'Crear reservación',
    customer: 'Cliente',
    selectCustomer: 'Selecciona un cliente',
    dog: 'Perro',
    selectDog: 'Selecciona un perro',
    checkIn: 'Entrada',
    checkOut: 'Salida',
    service: 'Servicio',
    selectService: 'Selecciona un servicio',
    kennel: 'Cabaña',
    room: 'Habitación',
    notes: 'Notas',
    debug: 'Depuración de hotel',
    board: 'Tablero de hotel',
    loading: 'Cargando hotel',
    emptyTitle: 'Aún no hay reservaciones de hotel',
    emptyBody: 'Crea una reservación para empezar a gestionar entradas.',
    dates: 'Fechas',
    location: 'Ubicación',
    unassigned: 'Sin asignar',
    dateRangeConnector: 'a',
    deleteConfirm: (name: string) => `¿Eliminar el hotel de ${name}? ${copy.common.cannotUndo}`,
    deleteLabel: (name: string) => `Eliminar el hotel de ${name}`,
  },
  services: {
    title: 'Servicios',
    body: 'Guardería, hospedaje, estética, transporte y servicios extra configurables.',
    newService: 'Nuevo servicio',
    create: 'Crear servicio',
    catalog: 'Catálogo de servicios',
    loading: 'Cargando servicios',
    emptyTitle: 'Aún no hay servicios',
    emptyBody: 'Crea definiciones de servicios antes de agregar reservaciones.',
    noDescription: 'Sin descripción',
    deleteConfirm: (name: string) => `¿Eliminar ${name}? ${copy.common.cannotUndo}`,
    deleteLabel: (name: string) => `Eliminar ${name}`,
  },
  users: {
    title: 'Usuarios',
    body: 'Acceso del equipo, roles y estado de cuentas.',
    adminRequired: 'Se requiere acceso de administrador.',
    invite: 'Invitar usuario del equipo',
    inviteUser: 'Invitar usuario',
    directory: 'Directorio del equipo',
    loading: 'Cargando usuarios',
    emptyTitle: 'No hay usuarios del equipo',
    emptyBody: 'Invita usuarios del equipo para otorgar acceso a la app.',
    roleFor: (name: string) => `Rol de ${name}`,
    disable: 'Deshabilitar',
    enable: 'Habilitar',
  },
};

const sexLabels: Record<string, string> = {
  male: 'Macho',
  female: 'Hembra',
  unknown: 'Desconocido',
};

const dogStatusLabels: Record<string, string> = {
  active: 'Activo',
  inactive: 'Inactivo',
  deceased: 'Fallecido',
};

const stayStatusLabels: Record<string, string> = {
  reserved: 'Reservado',
  'checked-in': 'Registrado',
  'checked-out': 'Finalizado',
  cancelled: 'Cancelado',
};

const serviceCategoryLabels: Record<string, string> = {
  daycare: 'Guardería',
  boarding: 'Hospedaje',
  grooming: 'Estética',
  spa: 'Spa',
  transportation: 'Transporte',
  medical: 'Veterinaria',
  treats: 'Premios',
  fee: 'Cargo',
  other: 'Otro',
};

const staffRoleLabels: Record<StaffRole, string> = {
  Administrator: 'Administrador',
  Receptionist: 'Recepción',
  Staff: 'Equipo',
};

const alertLevelLabels: Record<string, string> = {
  info: 'Información',
  warning: 'Advertencia',
  critical: 'Crítica',
};

const staffStatusLabels: Record<string, string> = {
  CONFIRMED: 'Confirmado',
  UNCONFIRMED: 'Sin confirmar',
  ARCHIVED: 'Archivado',
  COMPROMISED: 'Comprometido',
  UNKNOWN: 'Desconocido',
  RESET_REQUIRED: 'Restablecimiento requerido',
  FORCE_CHANGE_PASSWORD: 'Debe cambiar contraseña',
};

const withFallback = (labels: Record<string, string>, value?: string) => (value ? labels[value] ?? value : copy.common.unknown);

export const labelForSex = (value?: string) => withFallback(sexLabels, value);
export const labelForDogStatus = (value?: string) => withFallback(dogStatusLabels, value);
export const labelForStayStatus = (value?: string) => withFallback(stayStatusLabels, value);
export const labelForServiceCategory = (value?: string) => withFallback(serviceCategoryLabels, value);
export const labelForStaffRole = (value: StaffRole | string) => staffRoleLabels[value as StaffRole] ?? value;
export const labelForAlertLevel = (value?: string) => withFallback(alertLevelLabels, value);
export const labelForStaffStatus = (value?: string) => withFallback(staffStatusLabels, value);
